import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";

export const ConciliarStripe = ({ sedeId = "", onHecho }) => {
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [datos, setDatos] = useState(null);

  const consultar = async (aplicar) => {
    setCargando(true);
    try {
      const { data } = await api.post("/cobros/stripe/conciliar", { sede_id: sedeId, aplicar });
      setDatos(data);
      if (aplicar) {
        toast.success(data.total
          ? `${data.total} cobro(s) recuperados por ${data.importe} €`
          : "No había ningún cobro pendiente de recuperar");
        onHecho?.();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setCargando(false);
    }
  };

  const abrir = () => {
    setDatos(null);
    setAbierto(true);
    consultar(false);
  };

  return (
    <>
      <Button variant="outline" className="rounded-full font-semibold" onClick={abrir}
        data-testid="conciliar-stripe-btn">
        <RefreshCw className="h-4 w-4 mr-2" /> Revisar cobros en Stripe
      </Button>

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="rounded-2xl max-w-3xl" data-testid="conciliar-stripe-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Cobros cobrados en Stripe que faltan aquí</DialogTitle>
          </DialogHeader>

          {cargando && !datos ? (
            <div className="flex items-center gap-3 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Consultando Stripe sede por sede…
            </div>
          ) : !datos ? null : (
            <div className="grid gap-4">
              <p className="text-sm text-muted-foreground">
                {datos.total === 0
                  ? "Todo cuadra: cada cobro de Stripe tiene su apunte y su factura."
                  : `Se han encontrado ${datos.total} cobro(s) por ${datos.importe} € que están `
                    + "cobrados en Stripe pero no llegaron a registrarse. Al importarlos se emite su "
                    + "factura con la fecha real del pago (en serie aparte) y se recalcula el periodo "
                    + "pagado de cada alumno."}              </p>

              {datos.cobros.length > 0 && (
                <div className="max-h-80 overflow-y-auto grid gap-2" data-testid="conciliar-lista">
                  {datos.cobros.map((c) => (
                    <div key={c.factura_stripe}
                      className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{c.alumnos || "—"}</p>
                        <p className="text-xs text-muted-foreground">
                          {c.fecha} · {c.sede} · {c.matriculas}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-display font-extrabold">{c.importe} €</span>
                        {c.aplicado && (
                          <Badge className="rounded-full bg-emerald-600 hover:bg-emerald-600 text-white">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            {c.factura || "importado"}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {datos.huerfanas?.length > 0 && (
                <div className="grid gap-1.5" data-testid="conciliar-huerfanas">
                  <p className="text-xs font-semibold text-muted-foreground">
                    Suscripciones en Stripe que no se han podido asociar a ningún alumno
                    ({datos.huerfanas.length}):
                  </p>
                  {datos.huerfanas.slice(0, 10).map((h) => (
                    <p key={h.suscripcion} className="text-xs text-muted-foreground">
                      {h.sede} · {h.suscripcion} · {h.estado}
                    </p>
                  ))}
                </div>
              )}

              {datos.errores?.length > 0 && (
                <div className="grid gap-1.5" data-testid="conciliar-errores">
                  {datos.errores.map((e, i) => (
                    <p key={i} className="text-xs text-amber-600 flex items-start gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>{e.sede}: {e.detalle}</span>
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {datos && datos.total > 0 && !datos.aplicado && (
              <Button className="rounded-full font-semibold" disabled={cargando}
                onClick={() => consultar(true)} data-testid="conciliar-aplicar-btn">
                {cargando ? <Loader2 className="h-4 w-4 animate-spin" />
                  : `Importar ${datos.total} cobro(s)`}
              </Button>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => setAbierto(false)}
              data-testid="conciliar-cerrar-btn">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
